"use client";

import { Typography } from "./typography";
import { ChartContainer, ChartConfig } from "./chart";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { cn, formatPercentage } from "@drpcrd/utils";

type BarChartData = {
  label: string;
  value: number;
};

type ColorConfig = {
  barFill?: string;
  barBackground?: string;
};

type Props = {
  data: BarChartData[];
  title?: string;
  config?: ChartConfig;
  valueFormatter?: (value: number) => string;
  className?: string;
  maxHeight?: string;
  showPercentage?: boolean;
  colors?: ColorConfig;
  proportional?: boolean;
  totalValue?: number;
  /** Text size of the labels and values. */
  labelSize?: "sm" | "base";
};

/**
 * A reusable bar chart component that provides consistent styling for vertical bar charts
 */
export const BarChart = ({
  data,
  title,
  config = {},
  valueFormatter,
  className,
  maxHeight = "192px",
  showPercentage = true,
  colors = {
    barFill: "var(--color-primary)",
    barBackground: "var(--color-neutral-200)",
  },
  totalValue,
  labelSize = "base",
}: Props) => {
  const defaultConfig = {
    [title || "default"]: {
      label: title || "Data",
      color: colors.barFill,
    },
    ...config,
  };

  // Calculate the sum of all values to use for proportional display
  const sum = totalValue ?? data.reduce((acc, item) => acc + item.value, 0);

  const displayData = data.map((item) => ({
    ...item,
    originalValue: item.value,
    value:
      sum > 0
        ? showPercentage
          ? (item.value / sum).toFixed(4)
          : item.value
        : 0,
  }));

  const formatValue = (value: number): string => {
    if (valueFormatter) {
      return valueFormatter(value);
    } else if (showPercentage) {
      return formatPercentage(value, 1);
    } else {
      return value.toString();
    }
  };

  return (
    <ChartContainer
      config={defaultConfig}
      className={cn("h-full w-full", className)}
      style={{ height: maxHeight }}
    >
      <RechartsBarChart
        accessibilityLayer
        data={displayData}
        margin={{ top: 0, left: -15, right: -15, bottom: -15 }}
      >
        <CartesianGrid vertical={false} horizontal={false} />
        <XAxis
          xAxisId={0}
          dataKey="label"
          tick={({ x, y, payload }) => (
            <g transform={`translate(${x},${y})`}>
              <text x={0} y={0} textAnchor="middle">
                <Typography size={labelSize} weight="semibold" as="tspan">
                  {payload.value}
                </Typography>
              </text>
            </g>
          )}
          tickMargin={16}
          tickLine={false}
          axisLine={false}
        />
        <XAxis
          xAxisId={1}
          dataKey="value"
          interval={0}
          tick={({ x, y, payload }) => (
            <g transform={`translate(${x},${y})`}>
              <text x={0} y={0} textAnchor="middle">
                <Typography size={labelSize} weight="normal" as="tspan">
                  {formatValue(payload.value)}
                </Typography>
              </text>
            </g>
          )}
          tickMargin={6}
          tickLine={false}
          axisLine={false}
        />
        <YAxis hide domain={[0, "auto"]} allowDataOverflow />

        <Bar
          isAnimationActive={false}
          dataKey="value"
          fill={colors.barFill}
          background={{ fill: colors.barBackground }}
        />
      </RechartsBarChart>
    </ChartContainer>
  );
};
